import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Sparkles,
  MessageCircle,
} from 'lucide-react';
import ProductCard from '../ProductCard';
import { collection, getDocs, limit, orderBy, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../../utils/firebase';

import LoadingScreen from '../LoadingScreen';

/* =========================================================================
   PREMIUM CLOCHE LOADER (FULL-SCREEN & INLINE SUPPORT)
   ========================================================================= */
function BoxOpeningLoader({ isFullScreen = false }) {
  return <LoadingScreen isFullScreen={isFullScreen} />;
}

/* =========================================================================
   PREMIUM REVEAL-ON-SCROLL COMPONENT (HIGH PERFORMANCE GPU OPTIMIZED)
   ========================================================================= */
function RevealOnScroll({ children, delay = 0, className = '' }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.05, rootMargin: '0px 0px -20px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        transitionDelay: `${delay}ms`,
        transitionDuration: '500ms',
        transitionProperty: 'opacity, transform',
        transitionTimingFunction: 'cubic-bezier(0.25, 1, 0.5, 1)',
        willChange: 'opacity, transform'
      }}
      className={`transform-gpu ${isVisible
        ? 'opacity-100 translate-y-0'
        : 'opacity-0 translate-y-6'
        } ${className}`}
    >
      {children}
    </div>
  );
}

export default function HomePage({
  handleAddToCart,
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  selectedSubcategory,
  setSelectedSubcategory,
  selectedSectionId,
  setSelectedSectionId,
  selectedSectionName,
  setSelectedSectionName,
  myList,
  handleToggleMyList,
  cartCount,
  onCartClick,
  onStoreLocationsClick,
  onAuthClick
}) {
  const [promoAds, setPromoAds] = useState([]);
  const [currentAdIdx, setCurrentAdIdx] = useState(0);
  const [showMyListOnly, setShowMyListOnly] = useState(false);

  // FULL-SCREEN INITIAL LOADER STATE (Displays Box Opening animation on load)
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Firestore Sections & Products
  const [sections, setSections] = useState([]);
  const [sectionProducts, setSectionProducts] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Departments (Types) for round category buttons
  const [navTypes, setNavTypes] = useState([]);

  // Show Box Opening Unboxing animation for 1.8 seconds on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const bannersRef = collection(db, 'banners');
        const q = query(bannersRef, where('status', '==', true), orderBy('order', 'asc'));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const fetchedBanners = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              tag: data.tag || '',
              title: data.title || '',
              subtitle: data.subtitle || '',
              linkText: data.linkText || '',
              linkUrl: data.link || '',
              actionType: data.type || 'blank',
              whatsappNumber: data.whatsapp_number || '',
              whatsappMessage: data.whatsappMessage || '',
              mainImage: data.img_url || '',
              desktopImage: data.desktop_img_url || data.img_url || '',
              mobileImage: data.mobile_img_url || '',
              ...data
            };
          });
          setPromoAds(fetchedBanners);
        }
      } catch (error) {
        console.error("Error fetching banners:", error);
      }
    };
    fetchBanners();
  }, []);

  // Fetch departments (types) for round bubbles
  useEffect(() => {
    const fetchNavTypes = async () => {
      try {
        const typesSnap = await getDocs(query(collection(db, 'types'), orderBy('order')));
        const loaded = typesSnap.docs.map(typeDoc => {
          const typeData = typeDoc.data();
          return {
            id: typeDoc.id,
            name: typeData.title || typeData.name || typeDoc.id,
            image: typeData.img_url || typeData.image || ''
          };
        });
        setNavTypes(loaded);
      } catch (e) {
        console.warn('Could not fetch nav types for homepage bubbles:', e);
      }
    };
    fetchNavTypes();
  }, []);

  // Default Banner Content Carousel
  const defaultBanners = [
    {
      id: '1',
      tag: 'Organic Produce',
      title: 'Various Vegetables & Fresh Fruits Wholesale Supply',
      subtitle: 'Direct farm-to-kitchen sourcing & guaranteed daily 24/48h delivery for hotels & bakeries',
      linkText: 'Explore Catalog',
      linkUrl: '/products',
      image: '/dummy_horeca_banner.png'
    },
    {
      id: '2',
      tag: 'Weekly Deal',
      title: 'Fresh Vegetables Crates Big Discount',
      subtitle: 'Up to 30% off bulk crate orders for high-volume commercial kitchens & cafes',
      linkText: 'Shop Deals',
      linkUrl: '/products',
      image: '/banner_vegetables.png'
    },
    {
      id: '3',
      tag: 'Special Offer',
      title: 'Experience The Best Gourmet Fruits',
      subtitle: 'Handpicked premium fruits & eco-friendly food packaging at direct factory rates',
      linkText: 'Claim Offer',
      linkUrl: '/products',
      image: '/banner_fruits.png'
    }
  ];

  const bannersToDisplay = promoAds.length > 0 ? promoAds : defaultBanners;

  // Auto-play slides every 5s
  useEffect(() => {
    if (bannersToDisplay.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentAdIdx((prev) => (prev === bannersToDisplay.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [currentAdIdx, bannersToDisplay.length]);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    setIsLoading(true);
    try {
      const sectionRef = collection(db, "sections");
      const q = query(sectionRef, orderBy("order"), limit(5));
      const [snapshot] = await Promise.all([
        getDocs(q),
        getCountFromServer(collection(db, "products"))
      ]);
      const fetchedSections = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSections(fetchedSections);
    } catch (error) {
      console.error("Error fetching sections:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sections.length === 0) return;

    const fetchProductsForSections = async () => {
      const productsMap = {};
      try {
        await Promise.all(
          sections.map(async (section) => {
            const productsRef = collection(db, "products");
            const q = query(
              productsRef,
              where("sectionIds", "array-contains", section.id),
              limit(6)
            );
            const snapshot = await getDocs(q);
            const products = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            productsMap[section.id] = products;
          })
        );
        setSectionProducts(productsMap);
      } catch (error) {
        console.error("Error fetching products for sections:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductsForSections();
  }, [sections]);

  const filteredProducts = showMyListOnly ? myList : [];
  const currentBanner = bannersToDisplay[currentAdIdx] || defaultBanners[0];

  // EXECUTIVE HORECA PALETTE: High-performance color tokens (no backdrop blur)

  const enterpriseCtaBlock = (
    <RevealOnScroll delay={100} className="mt-8 mb-4">
      <div
        className="relative overflow-hidden p-8 md:p-14 flex flex-col md:flex-row items-center justify-between gap-10"
        style={{
          background: '#2F5D50',
          borderRadius: '4px',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {/* Subtle diagonal pattern */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #ffffff 0px, transparent 1px, transparent 28px, #ffffff 29px)' }} />

        <div className="relative z-10 space-y-4 text-center md:text-left max-w-xl">
          {/* Sage label */}
          <span
            className="inline-flex items-center gap-2 text-[10px] font-bold px-3 py-1.5 uppercase tracking-[0.15em]"
            style={{
              background: 'rgba(139,168,136,0.20)',
              color: '#B8D4B5',
              border: '1px solid rgba(139,168,136,0.30)',
              borderRadius: '2px',
            }}
          >
            <Sparkles className="w-3 h-3" /> Enterprise Procurement
          </span>
          <h3
            className="text-2xl md:text-3xl font-bold tracking-tight leading-snug m-0"
            style={{ color: '#F8F7F3' }}
          >
            Custom Branding &amp; Bulk Shipments
          </h3>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(247,244,238,0.65)' }}>
            Partner with ChefCo for dedicated enterprise account management, customized brand prints on eco packaging, and automated recurring delivery schedules.
          </p>
        </div>

        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-3 shrink-0">
          {/* Primary CTA — Burnt Terracotta */}
          <Link
            to="/products"
            className="font-bold text-sm px-7 py-3.5 no-underline flex items-center gap-2 transition-all duration-200"
            style={{ background: '#C46A42', color: '#ffffff', borderRadius: '4px' }}
            onMouseEnter={e => e.currentTarget.style.background = '#B75A37'}
            onMouseLeave={e => e.currentTarget.style.background = '#C46A42'}
          >
            <span>Browse Products</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
          {/* Secondary CTA — ghost */}
          <a
            href="https://wa.me/?text=Hello%20ChefCo%20Procurement%20Team%2C%20I%20want%20to%20discuss%20a%20bulk%20contract."
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-sm px-6 py-3.5 no-underline flex items-center gap-2 transition-all duration-200"
            style={{ background: 'rgba(247,244,238,0.08)', color: '#F8F7F3', border: '1px solid rgba(247,244,238,0.20)', borderRadius: '4px' }}
          >
            <MessageCircle className="w-4 h-4" style={{ color: '#8BA888' }} />
            <span>WhatsApp Quote</span>
          </a>
        </div>
      </div>
    </RevealOnScroll>
  );

  return (
    <div className="homepage-wrapper min-h-screen" style={{ background: '#F8F7F3', color: '#243126' }}>

      {/* ================= FULL-SCREEN INITIAL BOX OPENING UNBOXING LOADER ================= */}
      {isInitialLoading && <BoxOpeningLoader isFullScreen={true} />}

      <style>{`
        .chefco-section-card {
          transition: transform 0.3s cubic-bezier(0.25,1,0.5,1), box-shadow 0.3s ease;
        }
        .chefco-section-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(47,93,80,0.08) !important;
        }
        .dep-link .dep-icon {
          transition: transform 0.3s cubic-bezier(0.25,1,0.5,1);
        }
        .dep-link:hover .dep-icon { transform: translateY(-4px); }
      `}</style>

      <div className="homepage-container mx-auto px-2.5 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-10 pb-20 sm:pb-8 relative z-10" style={{ maxWidth: '1700px' }}>
        {/* ================= SINGLE HERO BANNER (REVEAL-ON-SCROLL) ================= */}
        <RevealOnScroll delay={0}>
          <div className="w-full aspect-[16/8] sm:aspect-[21/9] md:h-[300px] lg:h-[360px] relative overflow-hidden rounded-[20px] sm:rounded-[36px] shadow-[0_12px_40px_rgba(36,49,38,0.08)] group/hero bg-[#EEF1EC]">
            <Link
              to={currentBanner.linkUrl || '/products'}
              className="w-full h-full block"
            >
              <picture className="w-full h-full block">
                {currentBanner.mobileImage && (
                  <source media="(max-width: 640px)" srcSet={currentBanner.mobileImage} />
                )}
                <img
                  src={currentBanner.desktopImage || currentBanner.mainImage || currentBanner.image || defaultBanners[currentAdIdx % 3].image}
                  alt={currentBanner.title || 'ChefCo Wholesale Banner'}
                  className="w-full h-full block object-cover object-left transition-transform duration-700 ease-out group-hover/hero:scale-105"
                />
              </picture>
            </Link>

            {/* Banner Slider Navigation Arrows */}
            <button
              onClick={() => setCurrentAdIdx((prev) => (prev === 0 ? bannersToDisplay.length - 1 : prev - 1))}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-transparent text-white/90 hover:text-white hover:scale-110 border-none flex items-center justify-center transition-all duration-300 opacity-0 group-hover/hero:opacity-100 cursor-pointer drop-shadow-lg"
              aria-label="Previous Banner"
            >
              <ChevronLeft className="w-8 h-8 sm:w-10 sm:h-10 drop-shadow-md" />
            </button>
            <button
              onClick={() => setCurrentAdIdx((prev) => (prev === bannersToDisplay.length - 1 ? 0 : prev + 1))}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-transparent text-white/90 hover:text-white hover:scale-110 border-none flex items-center justify-center transition-all duration-300 opacity-0 group-hover/hero:opacity-100 cursor-pointer drop-shadow-lg"
              aria-label="Next Banner"
            >
              <ChevronRight className="w-8 h-8 sm:w-10 sm:h-10 drop-shadow-md" />
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center justify-center gap-2.5 z-20 bg-transparent px-3 sm:px-4 py-1.5 sm:py-2">
              {bannersToDisplay.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentAdIdx(idx)}
                  className={`h-2 sm:h-2.5 rounded-full transition-all duration-300 cursor-pointer border-none shadow-sm ${idx === currentAdIdx
                    ? 'w-6 sm:w-8 bg-white'
                    : 'w-2 sm:w-2.5 bg-white/60 hover:bg-white/90'
                    }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </RevealOnScroll>



        {/* ================= ITEM DISPLAYING CONTAINERS ================= */}
        <div className="space-y-4 pt-1">
          {/* ================= ABOUT CHEFCO SECTION ================= */}
          <RevealOnScroll delay={100}>
            <div className="my-1 sm:my-2 px-1 text-left flex flex-col items-start max-w-3xl">
              <h2 className="text-lg sm:text-2xl font-black text-[#243126] leading-tight mb-1.5">
                CHEFCO HOSPITALITY PARTNERS
              </h2>
              <p className="text-[#5D6962] text-xs sm:text-sm leading-relaxed font-medium">
                ChefCo partners with restaurants, hotels, cafés, bakeries, and institutional kitchens to simplify sourcing through premium packaging, wholesale products.
              </p>
            </div>
          </RevealOnScroll>
          {/* ================= DEPARTMENT CATEGORY CHIPS ================= */}
          <RevealOnScroll delay={0}>
            <div className="flex items-start justify-center gap-4 sm:gap-6 overflow-x-auto [&::-webkit-scrollbar]:hidden py-2 px-1 w-full">
              {navTypes.length > 0 ? navTypes.map((type, idx) => (
                <Link
                  key={type.id || idx}
                  to={`/products?category=${encodeURIComponent(type.name)}`}
                  className="dep-link flex flex-col items-center gap-2 shrink-0 cursor-pointer border-none bg-transparent no-underline group"
                >
                  {/* Circular icon with warm neutral bg */}
                  <div
                    className="dep-icon w-[90px] h-[90px] sm:w-[108px] sm:h-[108px] md:w-[124px] md:h-[124px] flex items-center justify-center overflow-hidden relative shadow-[0_4px_16px_rgba(36,49,38,0.08)] group-hover:shadow-[0_10px_25px_rgba(47,93,80,0.18)] transition-all duration-300"
                    style={{
                      background: '#FFFFFF',
                      border: '2px solid #D7DDD4',
                      borderRadius: '50%',
                    }}
                  >
                    {type.image || type.imageUrl || type.img ? (
                      <img src={type.image || type.imageUrl || type.img} alt={type.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" style={{ borderRadius: '50%' }} />
                    ) : (
                      <span className="text-base sm:text-lg font-extrabold tracking-wider" style={{ color: '#2F5D50' }}>
                        {type.name.substring(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span
                    className="text-xs sm:text-sm font-bold tracking-wide text-center leading-tight transition-colors duration-200 group-hover:text-[#2F5D50]"
                    style={{ color: '#5D6962', maxWidth: 110 }}
                  >
                    {type.name}
                  </span>
                </Link>
                )) : (
                  <div className="text-sm py-4 px-2" style={{ color: '#8B958E' }}>Loading departments...</div>
                )}
              </div>
          </RevealOnScroll>


       

          {/* 3D BOX OPENING LOADER STATE (INLINE) */}
          {isLoading ? (
            <BoxOpeningLoader isFullScreen={false} />
          ) : (!showMyListOnly && sections.length > 0) || (showMyListOnly && myList.length > 0) ? (
            <div className="space-y-8">
              {!showMyListOnly && sections.length > 0 && Object.keys(sectionProducts).length > 0 ? (
                sections.map((section, sIdx) => {
                  const products = sectionProducts[section.id] || [];
                  if (products.length === 0) return null;

                  return (
                    <React.Fragment key={section.id}>
                      <RevealOnScroll delay={sIdx * 80}>
                      {(() => {
                        // Premium editorial accent system — Forest Green primary, Sage alternate
                        const accent = sIdx % 2 === 0 ? '#2F5D50' : '#8BA888';
                        return (
                          <div
                            className="chefco-section-card flex flex-col relative overflow-hidden p-3.5 sm:p-6 lg:p-7"
                            style={{
                              background: sIdx % 3 === 1 ? '#F2ECE4' : '#FFFFFF',
                              border: '1px solid #D7DDD4',
                              borderRadius: '4px',
                              boxShadow: '0 1px 3px rgba(36,49,38,0.03)',
                            }}
                          >
                            {/* Left accent bar — Forest Green or Sage */}
                            <div className="absolute top-0 left-0 w-[3px] h-full" style={{ background: accent }} />

                            {/* Section Header */}
                            <div
                              className="flex flex-wrap items-center justify-between gap-2 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-[#D7DDD4]"
                            >
                              <div className="flex items-center gap-3">
                                <h4
                                  className="text-xs sm:text-sm font-bold uppercase tracking-[0.1em] m-0"
                                  style={{ color: '#243126' }}
                                >
                                  {section.name}
                                </h4>
                              </div>

                              <Link
                                to={`/products?sectionId=${section.id}&sectionName=${encodeURIComponent(section.name)}`}
                                className="inline-flex items-center gap-1.5 text-[11px] font-semibold no-underline transition-all duration-200"
                                style={{ color: '#2F5D50' }}
                              >
                                <span>View all</span>
                                <ChevronRight className="w-3 h-3" />
                              </Link>
                            </div>

                            {/* Products grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3.5">
                              {products.slice(0, 6).map((product, idx) => (
                                <ProductCard
                                  key={idx}
                                  product={product}
                                  onAddToCart={handleAddToCart}
                                  isLiked={myList.includes(product.id) || (product.code && myList.includes(product.code)) || (product.title && myList.includes(product.title))}
                                  onToggleMyList={() => handleToggleMyList(product.id || product.code || product.title)}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      </RevealOnScroll>
                      {sIdx === 2 && enterpriseCtaBlock}
                      {sections.length < 3 && sIdx === sections.length - 1 && enterpriseCtaBlock}
                    </React.Fragment>
                  );
                })
              ) : (
                Object.entries(
                  filteredProducts.reduce((acc, product) => {
                    const category = product.category || 'Other';
                    if (!acc[category]) {
                      acc[category] = [];
                    }
                    acc[category].push(product);
                    return acc;
                  }, {})
                ).map(([category, items], cIdx) => (
                  <RevealOnScroll key={category} delay={cIdx * 80}>
                    <div className="bg-white rounded-[32px] p-3.5 sm:p-6 lg:p-8 border border-[#F4F1EC] shadow-[0_20px_50px_rgba(20,47,36,0.04)] flex flex-col relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 via-emerald-400 to-[#2CB742]" />
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3.5 border-b border-[#D7DDD4]">
                        <div className="flex items-center gap-3">
                          <div className="w-1.5 h-7 bg-[#2CB742] rounded-full shrink-0 shadow-[0_0_12px_rgba(44,183,66,0.6)]" />
                          <h4 className="text-lg sm:text-xl font-black text-[#243126] uppercase tracking-wide m-0">
                            {category}
                          </h4>
                        </div>

                        <Link
                          to={`/products?category=${encodeURIComponent(category)}`}
                          className="group/all inline-flex items-center gap-2 bg-[#EEF1EC] hover:bg-[#2F5D50] hover:text-white text-[#2F5D50]  px-4 py-2 rounded-xl text-xs font-black transition-all duration-300 no-underline border border-[#2F5D50]/20"
                        >
                          <span>View All</span>
                          <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover/all:translate-x-1" />
                        </Link>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3.5 pt-2">
                        {items.slice(0, 6).map((product, idx) => (
                          <ProductCard
                            key={idx}
                            product={product}
                            onAddToCart={handleAddToCart}
                            isLiked={myList.includes(product.id) || (product.code && myList.includes(product.code)) || (product.title && myList.includes(product.title))}
                            onToggleMyList={() => handleToggleMyList(product.id || product.code || product.title)}
                          />
                        ))}
                      </div>
                    </div>
                  </RevealOnScroll>
                ))
              )}
            </div>
          ) : (
            <RevealOnScroll delay={0}>
              <div className="bg-[#FCFAF8] border border-[#EEF1EC] rounded-[40px] p-12 sm:p-16 text-center shadow-2xl max-w-2xl mx-auto">
                <div className="w-20 h-20 bg-[#EEF1EC] rounded-3xl flex items-center justify-center text-4xl mx-auto mb-4 text-[#2F5D50]">📦</div>
                <h3 className="text-xl font-extrabold text-[#243126]">No wholesale products found</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-2 max-w-md mx-auto leading-relaxed font-medium">
                  We couldn't find anything matching your search query. Try clearing your filters or exploring our full wholesale catalog!
                </p>
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setSelectedSubcategory(null);
                    setSearchQuery('');
                    setShowMyListOnly(false);
                  }}
                  className="mt-6 font-bold text-xs px-7 py-3 transition cursor-pointer border-none"
                  style={{ background: '#2F5D50', color: '#ffffff', borderRadius: '4px' }}
                >
                  Reset Filters & View All
                </button>
              </div>
            </RevealOnScroll>
          )}
        </div>



      </div>
    </div>
  );
}
